import { getFromIDB, putToIDB } from "metabase/jerry/Common/indexed-db";

import { MAX_TABS, STORAGE_KEY_PREFIX } from "../constants";
import type {
  PersistedAdhocTab,
  PersistedWorkbench,
  WorkbenchState,
} from "../types";
import { ADHOC_STORAGE_VERSION } from "../types";

/**
 * Storage is scoped per user: shared workstations and account switching are
 * real, and SQL text can leak table and column names the next user isn't
 * allowed to see.
 */
export function getStorageKey(userId: number | undefined | null): string {
  return `${STORAGE_KEY_PREFIX}.v${ADHOC_STORAGE_VERSION}.u${userId ?? "anon"}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isValidPersistedTab(value: unknown): value is PersistedAdhocTab {
  if (!isRecord(value)) {
    return false;
  }
  const { id, name, databaseId, sql } = value;
  return (
    typeof id === "string" &&
    typeof name === "string" &&
    typeof sql === "string" &&
    (databaseId === null || typeof databaseId === "number")
  );
}

/**
 * Bring an unknown persisted payload up to the current version.
 *
 * Returns `null` for anything we can't understand — including payloads written
 * by a *newer* version of the app, which a downgraded client must not try to
 * interpret.
 */
function migrate(
  payload: Record<string, unknown>,
): Record<string, unknown> | null {
  return payload.version === ADHOC_STORAGE_VERSION ? payload : null;
}

/**
 * Validate a persisted workbench, tolerating anything.
 *
 * A corrupt or stale payload must degrade to "start fresh", never to a broken
 * page, so every failure path returns `null`.
 */
export function parseWorkbench(stored: unknown): PersistedWorkbench | null {
  if (!isRecord(stored)) {
    return null;
  }
  const migrated = migrate(stored);
  if (migrated == null) {
    return null;
  }
  // Drop individual malformed tabs rather than discarding the whole session.
  const tabs = (Array.isArray(migrated.tabs) ? migrated.tabs : [])
    .filter(isValidPersistedTab)
    .slice(0, MAX_TABS);
  if (tabs.length === 0) {
    return null;
  }
  const activeTabId = tabs.some((tab) => tab.id === migrated.activeTabId)
    ? (migrated.activeTabId as string)
    : tabs[0].id;
  return {
    version: ADHOC_STORAGE_VERSION,
    tabs,
    activeTabId,
    nextTabIndex:
      typeof migrated.nextTabIndex === "number" &&
      migrated.nextTabIndex > tabs.length
        ? migrated.nextTabIndex
        : tabs.length + 1,
    savedAt: typeof migrated.savedAt === "number" ? migrated.savedAt : 0,
  };
}

export async function loadWorkbench(
  userId: number | undefined | null,
): Promise<PersistedWorkbench | null> {
  try {
    return parseWorkbench(await getFromIDB(getStorageKey(userId)));
  } catch {
    return null;
  }
}

/**
 * The persistable slice of the workbench — results are deliberately excluded.
 *
 * SQL is stored whole. IndexedDB is measured against a disk-backed quota
 * rather than localStorage's ~5MB origin cap, so there is no reason to
 * truncate a query and hand the user back a mangled statement on reload.
 */
export function toPersistedWorkbench(
  state: WorkbenchState,
  savedAt: number,
): PersistedWorkbench {
  return {
    version: ADHOC_STORAGE_VERSION,
    tabs: state.tabs.slice(0, MAX_TABS),
    activeTabId: state.activeTabId,
    nextTabIndex: state.nextTabIndex,
    savedAt,
  };
}

/**
 * Write the workbench, failing silently.
 *
 * Storage can be unavailable (private browsing) or evicted under disk
 * pressure. Neither is worth interrupting someone mid-query over, and nothing
 * already on screen is lost when it happens.
 */
export async function saveWorkbench(
  userId: number | undefined | null,
  payload: PersistedWorkbench,
): Promise<void> {
  try {
    await putToIDB(getStorageKey(userId), payload);
  } catch {
    // ignore
  }
}
